import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Loader2, Trash2, User, Mail, Phone, MapPin, UserCheck, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PhotoGallery } from '@/components/PhotoGallery';
import { SiteJournal } from '@/components/SiteJournal';
import { jobCategoryOptions } from '@/lib/jobCategory';

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('actif');
  const [assignedRepIds, setAssignedRepIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontractorIds, setSubcontractorIds] = useState([]);
  const [sourceLead, setSourceLead] = useState(null);
  const [createdAt, setCreatedAt] = useState('');
  const [jobCategory, setJobCategory] = useState('');

  const CLIENT_STATUSES = [
    { value: 'signe', label: tf('Signe', 'Signed'), color: 'bg-blue-500' },
    { value: 'travaux_en_cours', label: tf('Travaux en cours', 'Work in progress'), color: 'bg-orange-500' },
    { value: 'complete', label: tf('Complete', 'Completed'), color: 'bg-green-500' },
    { value: 'annule', label: tf('Annule', 'Cancelled'), color: 'bg-red-500' },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setEmail(data.email || '');
        setPhone(data.phone || '');
        setAddress(data.address || '');
        setCity(data.city || '');
        setDescription(data.description || '');
        setStatus(data.status || 'actif');
        setAssignedRepIds(data.assigned_representative_ids || (data.assigned_representative_id ? [data.assigned_representative_id] : []));
        setSubcontractorIds(data.subcontractor_ids || []);
        setCreatedAt(data.created_at || '');
        setJobCategory(data.job_category || '');
        if (data.source_lead_id) {
          setSourceLead(data.source_lead_id);
        }
      } catch {
        toast.error(tf('Client introuvable', 'Client not found'));
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const { data } = await api.get('/employees');
        setEmployees((data.employees || []).filter(e => e.status === 'actif'));
      } catch { /* ignore */ }
    };
    const loadSubcontractors = async () => {
      try {
        const { data } = await api.get('/subcontractors');
        setSubcontractors((data.subcontractors || []).filter(s => s.status === 'actif'));
      } catch { /* ignore */ }
    };
    loadEmployees();
    loadSubcontractors();
  }, []);

  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const formatPhone = (v) => {
    const d = v.replace(/[^\d]/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  };
  const isValidPhone = (p) => !p || /^\d{3}-\d{3}-\d{4}$/.test(p);

  const handleSave = async () => {
    if (email && !isValidEmail(email)) {
      toast.error(language === 'fr' ? 'Format de courriel invalide' : 'Invalid email format');
      return;
    }
    if (phone && !isValidPhone(phone)) {
      toast.error(language === 'fr' ? 'Numero de telephone invalide (ex: 514-555-1234)' : 'Invalid phone number');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}`, {
        first_name: firstName, last_name: lastName,
        email, phone, address, city, description, status,
        assigned_representative_ids: assignedRepIds,
        subcontractor_ids: subcontractorIds,
      });
      toast.success(tf('Client mis a jour', 'Client updated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || tf('Erreur', 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(tf('Supprimer ce client?', 'Delete this client?'))) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success(tf('Client supprime', 'Client deleted'));
      navigate('/clients');
    } catch {
      toast.error(tf('Erreur', 'Error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="client-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clients')} data-testid="back-to-clients">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight flex items-center gap-3">
              {firstName} {lastName}
              {(() => {
                const s = CLIENT_STATUSES.find(st => st.value === status);
                return s ? (
                  <Badge className={`${s.color} text-white text-xs`} data-testid="client-status-badge">
                    {s.label}
                  </Badge>
                ) : null;
              })()}
            </h1>
            {createdAt && (
              <p className="text-sm text-muted-foreground">
                {tf('Cree le', 'Created')} {new Date(createdAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                {sourceLead && (
                  <span className="ml-2 text-xs">
                    — <a href={`/leads/${sourceLead}`} className="text-primary hover:underline">
                      {tf('Voir le prospect source', 'View source prospect')}
                    </a>
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-client-btn">
          <Trash2 className="h-4 w-4 mr-2" />
          {tf('Supprimer', 'Delete')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            {tf('Informations du client', 'Client information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Prenom', 'First name')}</Label>
              <Input value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="edit-firstname" />
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Nom', 'Last name')}</Label>
              <Input value={lastName} onChange={e => setLastName(e.target.value)} data-testid="edit-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="edit-email" className={email && !isValidEmail(email) ? 'border-red-500' : ''} />
              {email && !isValidEmail(email) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format invalide (ex: nom@domaine.com)' : 'Invalid format'}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {tf('Telephone', 'Phone')}</Label>
              <Input value={phone} onChange={e => setPhone(formatPhone(e.target.value))} data-testid="edit-phone" className={phone && !isValidPhone(phone) ? 'border-red-500' : ''} />
              {phone && !isValidPhone(phone) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format: 514-555-1234' : 'Format: 514-555-1234'}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {tf('Adresse', 'Address')}</Label>
              <Input value={address} onChange={e => setAddress(e.target.value)} data-testid="edit-address" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {tf('Ville', 'City')}</Label>
              <Input value={city} onChange={e => setCity(e.target.value)} data-testid="edit-city" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Statut', 'Status')}</Label>
              <Select value={status} onValueChange={(val) => {
                if ((val === 'travaux_en_cours' || val === 'complete') && assignedRepIds.length === 0) {
                  toast.error(tf('Vous devez assigner un employé avant de changer le statut.', 'You must assign an employee before changing the status.'));
                  return;
                }
                setStatus(val);
              }}>
                <SelectTrigger data-testid="client-status-select">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const s = CLIENT_STATUSES.find(st => st.value === status);
                      return s ? <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} /> : null;
                    })()}
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {CLIENT_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5" /> {tf('Employés assignés', 'Assigned employees')} ({assignedRepIds.length})</Label>
              {employees.length === 0 ? (
                <p className="text-xs text-muted-foreground">{tf('Aucun employe actif', 'No active employees')}</p>
              ) : (
                <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1" data-testid="client-reps-list">
                  {employees.map(emp => (
                    <label
                      key={emp.employee_id}
                      className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-muted/50 transition-colors ${assignedRepIds.includes(emp.employee_id) ? 'bg-blue-50 border border-blue-200' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={assignedRepIds.includes(emp.employee_id)}
                        onChange={() => setAssignedRepIds(prev => prev.includes(emp.employee_id) ? prev.filter(id => id !== emp.employee_id) : [...prev, emp.employee_id])}
                        className="rounded accent-blue-500"
                        data-testid={`rep-checkbox-${emp.employee_id}`}
                      />
                      <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1.5" data-testid="client-job-category-section">
            <Label className="flex items-center gap-1.5">
              <Wrench className="h-3.5 w-3.5" /> {tf('Type de job', 'Job type')}
            </Label>
            <Select
              value={jobCategory || 'unset'}
              onValueChange={async (v) => {
                const next = v === 'unset' ? null : v;
                setJobCategory(next || '');
                try {
                  await api.put(`/clients/${clientId}`, { job_category: next });
                  toast.success(tf('Type de job mis a jour', 'Job type updated'));
                } catch (err) {
                  toast.error(err.response?.data?.detail || tf('Erreur', 'Error'));
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-80" data-testid="client-job-category-select">
                <SelectValue placeholder={tf('Choisir...', 'Choose...')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">{tf('Non spécifié', 'Not set')}</SelectItem>
                {jobCategoryOptions(language).map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`client-job-category-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> {tf('Sous-traitants assignes', 'Assigned subcontractors')} ({subcontractorIds.length})</Label>
            {subcontractors.length === 0 ? (
              <p className="text-xs text-muted-foreground">{tf('Aucun sous-traitant actif', 'No active subcontractors')}</p>
            ) : (
              <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1" data-testid="client-subcontractors-list">
                {subcontractors.map(sub => (
                  <label
                    key={sub.subcontractor_id}
                    className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-muted/50 transition-colors ${subcontractorIds.includes(sub.subcontractor_id) ? 'bg-orange-50 border border-orange-200' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={subcontractorIds.includes(sub.subcontractor_id)}
                      onChange={() => setSubcontractorIds(prev => prev.includes(sub.subcontractor_id) ? prev.filter(id => id !== sub.subcontractor_id) : [...prev, sub.subcontractor_id])}
                      className="rounded accent-orange-500"
                    />
                    <span className="text-sm">{sub.first_name} {sub.last_name}</span>
                    {sub.specialty && <Badge className="text-[10px] ml-auto" variant="outline">{sub.specialty}</Badge>}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Description / Notes</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} data-testid="edit-description" />
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-client-edit-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {tf('Sauvegarder', 'Save')}
          </Button>
        </CardContent>
      </Card>

      {/* Photos */}
      <PhotoGallery entityType="client" entityId={clientId} />

      {/* Journal de chantier */}
      <SiteJournal clientId={clientId} />
    </div>
  );
}
